import type { DeskDetail } from '@deskworks/shared-types';
import { Link } from '@tanstack/react-router';
import { KeyRound, Pencil, Plus, X } from 'lucide-react';
import { useState, type ReactNode } from 'react';
import { Field } from '@/components/field';
import { Section } from '@/components/section';
import { EmptyState } from '@/components/states';
import { Button } from '@/components/ui/button';
import { Checkbox } from '@/components/ui/checkbox';
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle, DialogTrigger } from '@/components/ui/dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { api } from '@/lib/api';
import { useApiMutation, useRunSecrets } from '@/lib/hooks';
import { t, tr } from '@/lib/i18n';
import { qk } from '@/lib/query-keys';

type Granted = DeskDetail['secrets'][number];

/** Secrets the desk gives its runs as environment variables (docs/features/secrets.md). */
export function DeskSecretsSection({ desk }: { desk: DeskDetail }) {
  const remove = useApiMutation((secretId: string) => api.desks.removeSecret(desk.id, secretId), {
    invalidate: [qk.desk(desk.id), qk.secrets(desk.workspaceId)],
    success: t('Credential removed from desk'),
  });
  const agentName = new Map(desk.agents.map((a) => [a.agentId, a.name]));

  return (
    <Section
      icon={<KeyRound />}
      title={t('Credentials')}
      description={t('Environment variables for the runs of this desk’s agents, e.g. an API key a script needs.')}
      actions={
        <SecretGrantDialog desk={desk}>
          <Button variant="outline" size="sm">
            <Plus /> {t('Add')}
          </Button>
        </SecretGrantDialog>
      }
    >
      {desk.secrets.length === 0 ? (
        <EmptyState compact title={t('No credentials')} description={t('Runs of this desk get no credentials.')} />
      ) : (
        <ul className="-mx-5 divide-y border-y">
          {desk.secrets.map((s) => (
            <li key={s.id} className="flex items-center gap-2 px-5 py-2.5">
              <div className="min-w-0 flex-1">
                <div className="truncate font-mono text-sm">{s.name}</div>
                <div className="truncate text-xs text-muted-foreground">
                  {s.agentIds === null ? t('All agents') : s.agentIds.map((id) => agentName.get(id) ?? t('unknown agent')).join(', ')}
                </div>
              </div>
              <SecretGrantDialog desk={desk} granted={s}>
                <Button variant="ghost" size="icon-sm" className="size-7" aria-label={t('Choose agents')} title={t('Choose agents')}>
                  <Pencil />
                </Button>
              </SecretGrantDialog>
              <Button
                variant="ghost"
                size="icon-sm"
                className="-mr-2 size-7"
                aria-label={t('Remove from desk')}
                title={t('Remove from desk')}
                onClick={() => remove.mutate(s.id)}
                disabled={remove.isPending}
              >
                <X />
              </Button>
            </li>
          ))}
        </ul>
      )}
    </Section>
  );
}

function SecretGrantDialog({ desk, granted, children }: { desk: DeskDetail; granted?: Granted; children: ReactNode }) {
  const secrets = useRunSecrets(desk.workspaceId);
  const [open, setOpen] = useState(false);
  const [pick, setPick] = useState(granted?.id ?? '');
  const [all, setAll] = useState(granted ? granted.agentIds === null : true);
  const [chosen, setChosen] = useState<Set<string>>(new Set(granted?.agentIds ?? []));
  const linked = new Set(desk.secrets.map((s) => s.id));
  const addable = (secrets.data ?? []).filter((s) => !linked.has(s.id));

  const save = useApiMutation(() => api.desks.putSecret(desk.id, pick, all ? null : [...chosen]), {
    invalidate: [qk.desk(desk.id), qk.secrets(desk.workspaceId)],
    success: granted ? t('Saved') : t('Credential added to desk'),
    onSuccess: () => {
      setOpen(false);
      if (!granted) {
        setPick('');
        setAll(true);
        setChosen(new Set());
      }
    },
  });

  const toggle = (agentId: string, on: boolean) =>
    setChosen((prev) => {
      const next = new Set(prev);
      if (on) next.add(agentId);
      else next.delete(agentId);
      return next;
    });

  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogTrigger asChild>{children}</DialogTrigger>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>{granted ? t('Who gets {name}?', { name: granted.name }) : t('Add credential')}</DialogTitle>
          <DialogDescription>
            {t('The chosen agents’ runs get the credential as environment variables in their shell (Claude and Codex subscriptions). The agent sees only the names.')}
          </DialogDescription>
        </DialogHeader>
        {!granted && secrets.isSuccess && addable.length === 0 ? (
          <EmptyState
            compact
            title={t('No more credentials')}
            description={tr('All credentials are already on this desk — or there aren’t any yet. {link}', {
              link: (
                <Link to="/w/$workspaceId/connections" params={{ workspaceId: desk.workspaceId }} className="text-primary hover:underline">
                  {t('Add credential')}
                </Link>
              ),
            })}
          />
        ) : (
          <div className="grid gap-4">
            {!granted && (
              <Field label={t('Credential')}>
                <Select value={pick} onValueChange={setPick}>
                  <SelectTrigger>
                    <SelectValue placeholder={t('Choose a credential')} />
                  </SelectTrigger>
                  <SelectContent>
                    {addable.map((s) => (
                      <SelectItem key={s.id} value={s.id}>
                        {s.name}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </Field>
            )}
            <div className="grid gap-2">
              <span className="text-sm font-medium">{t('Agents')}</span>
              <label className="flex cursor-pointer items-start gap-2.5 rounded-md border px-3 py-2.5">
                <Checkbox checked={all} onChange={(e) => setAll(e.target.checked)} className="mt-0.5" />
                <span className="text-sm">
                  <span className="font-medium">{t('All agents of this desk')}</span>
                  <span className="block text-xs text-muted-foreground">{t('Also agents added to the desk later.')}</span>
                </span>
              </label>
              {!all &&
                desk.agents.map((a) => (
                  <label key={a.agentId} className="flex cursor-pointer items-center gap-2.5 rounded-md border px-3 py-2">
                    <Checkbox checked={chosen.has(a.agentId)} onChange={(e) => toggle(a.agentId, e.target.checked)} />
                    <span className="text-sm">{a.name}</span>
                  </label>
                ))}
            </div>
          </div>
        )}
        <DialogFooter>
          <Button variant="outline" onClick={() => setOpen(false)}>
            {t('Cancel')}
          </Button>
          <Button disabled={!pick || (!all && chosen.size === 0)} loading={save.isPending} onClick={() => save.mutate()}>
            {granted ? t('Save') : t('Add')}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
